import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useMyLeaveRequests } from '../../hooks/useLeaveRequests';
import { LeaveRequestStatus } from '../../types';
import { leaveRequestsService } from '../../services/api/leaveRequests';
import StatusBadge from '../../components/common/StatusBadge';
import Loading from '../../components/common/Loading';
import ErrorMessage from '../../components/common/ErrorMessage';
import ConfirmDialog from '../../components/common/ConfirmDialog';
import { useToast } from '../../contexts/ToastContext';
import './MyLeaveRequests.css';

const MyLeaveRequests: React.FC = () => {
  const navigate = useNavigate();
  const { showSuccess, showError } = useToast();
  const [statusFilter, setStatusFilter] = useState<LeaveRequestStatus | undefined>();
  const { requests, loading, error, total, page, setPage, pageSize, refetch } =
    useMyLeaveRequests(statusFilter);
  const [submittingId, setSubmittingId] = useState<number | null>(null);
  const [submitConfirm, setSubmitConfirm] = useState<{ isOpen: boolean; requestId: number | null }>({
    isOpen: false,
    requestId: null,
  });

  const openSubmitConfirm = (id: number) => {
    setSubmitConfirm({ isOpen: true, requestId: id });
  };

  const closeSubmitConfirm = () => {
    setSubmitConfirm({ isOpen: false, requestId: null });
  };

  const handleSubmit = async () => {
    const id = submitConfirm.requestId;
    if (!id) return;

    try {
      setSubmittingId(id);
      await leaveRequestsService.submit(id);
      showSuccess('Leave request submitted for approval successfully!');
      await refetch();
      closeSubmitConfirm();
    } catch (err: any) {
      const errorMessage = err.response?.data?.message || err.message || 'Failed to submit request';
      showError(errorMessage);
      closeSubmitConfirm();
    } finally {
      setSubmittingId(null);
    }
  };

  if (loading) return <Loading text="Loading your leave requests..." />;
  if (error) return <ErrorMessage error={error} onRetry={refetch} />;

  return (
    <div className="my-leave-requests-page">
      <div className="page-header">
        <h1>My Leave Requests</h1>
        <button
          className="button button-primary"
          onClick={() => navigate('/leave-requests/new')}
        >
          + New Leave Request
        </button>
      </div>

      <div className="filters-bar">
        <select
          value={statusFilter || ''}
          onChange={(e) => setStatusFilter(e.target.value as LeaveRequestStatus || undefined)}
          className="filter-select"
        >
          <option value="">All Statuses</option>
          <option value={LeaveRequestStatus.DRAFT}>Draft</option>
          <option value={LeaveRequestStatus.PENDING}>Pending</option>
          <option value={LeaveRequestStatus.APPROVED_L1}>Approved L1</option>
          <option value={LeaveRequestStatus.APPROVED}>Approved</option>
          <option value={LeaveRequestStatus.REJECTED}>Rejected</option>
          <option value={LeaveRequestStatus.CANCELLED}>Cancelled</option>
        </select>
        <span className="results-count">{total} request(s)</span>
      </div>

      {requests.length === 0 ? (
        <div className="empty-state">
          <p>No leave requests found.</p>
          <button
            className="button button-primary"
            onClick={() => navigate('/leave-requests/new')}
          >
            Create Your First Request
          </button>
        </div>
      ) : (
        <div className="requests-list">
          {requests.map((request) => (
            <div key={request.id} className="request-card">
              <div className="request-header">
                <div className="request-dates">
                  <span className="date-badge">
                    {new Date(request.startDate).toLocaleDateString('en-GB')}
                  </span>
                  <span className="date-separator">→</span>
                  <span className="date-badge">
                    {new Date(request.endDate).toLocaleDateString('en-GB')}
                  </span>
                  <span className="days-count">
                    ({request.totalDays} {request.totalDays === 1 ? 'day' : 'days'})
                  </span>
                </div>
                <StatusBadge status={request.status} />
              </div>

              <div className="request-body">
                <div className="request-meta">
                  <span className="leave-type">{request.leaveType?.name}</span>
                  {(request.isLateSubmission === true || request.isLateSubmission === 1) && (
                    <span className="late-badge">⚠️ Late Submission</span>
                  )}
                  {request.isPaid !== null && request.isPaid !== undefined && (
                    <span className={`paid-badge ${request.isPaid === true || request.isPaid === 1 ? 'paid' : 'unpaid'}`}>
                      {request.isPaid === true || request.isPaid === 1 ? '✓ Paid' : '✗ Unpaid'}
                    </span>
                  )}
                </div>
                <p className="request-reason">{request.reason}</p>
              </div>

              <div className="request-actions">
                <button
                  className="button-link"
                  onClick={() => navigate(`/leave-requests/${request.id}`)}
                >
                  View Details
                </button>
                {request.status === LeaveRequestStatus.DRAFT && (
                  <>
                    <button
                      className="button-link"
                      onClick={() => navigate(`/leave-requests/${request.id}/edit`)}
                      disabled={submittingId === request.id}
                    >
                      Edit
                    </button>
                    <button
                      className="button button-primary button-small"
                      onClick={() => openSubmitConfirm(request.id)}
                      disabled={submittingId === request.id || submittingId !== null}
                    >
                      Submit for Approval
                    </button>
                  </>
                )}
              </div>
            </div>
          ))}
        </div>
      )}

      {total > pageSize && (
        <div className="pagination">
          <button
            disabled={page === 1}
            onClick={() => setPage(page - 1)}
            className="button button-secondary"
          >
            Previous
          </button>
          <span className="page-info">
            Page {page} of {Math.ceil(total / pageSize)}
          </span>
          <button
            disabled={page * pageSize >= total}
            onClick={() => setPage(page + 1)}
            className="button button-secondary"
          >
            Next
          </button>
        </div>
      )}

      <ConfirmDialog
        isOpen={submitConfirm.isOpen}
        onClose={closeSubmitConfirm}
        onConfirm={handleSubmit}
        title="Submit Leave Request"
        message="Are you sure you want to submit this leave request for approval? Once submitted, you won't be able to edit it."
        confirmText="Submit"
        cancelText="Cancel"
        variant="info"
        isLoading={submittingId !== null}
      />
    </div>
  );
};

export default MyLeaveRequests;